/**
 * useProjectPage.js
 */

import { useEffect, useState } from "react";
import { useGetProjects } from "../../hooks/Projects";

/**
 * useProjectPage is a custom hook that handles project-related logic for a specific site.
 * It manages fetching, updating, creating, and deleting projects while keeping track of
 * the active project and providing relevant data.
 *
 * @param {number} activeSiteId - The ID of the active site to which the projects belong.
 *
 * @returns {Object} An object containing the following properties:
 *   - {number|null} activeProjectId: The ID of the active project, or null if no project is selected.
 *   - {function} onProjectCreated: A callback function that is invoked when a project is created.
 *   - {function} onProjectDeleted: A callback function that is invoked when a project is deleted.
 *   - {function} onProjectUpdated: A callback function that is invoked when a project is updated.
 *   - {Array} projectLookup: A list of project objects used for displaying the projects.
 *   - {boolean} projectsLoading: A flag indicating if projects are being loaded.
 *   - {boolean} projectsError: A flag indicating if there was an error fetching the projects.
 *   - {function} selectActiveProject: A function to set the active project.
 */
export const useProjectPage = (activeSiteId) => {
  const [
    getProjects, {
      data: projectsData,
      error: projectsError,
      loading: projectsLoading,
    }
  ] = useGetProjects();

  const [activeProjectId, setActiveProjectId] = useState(null);
  const [projectLookup, setProjectLookup] = useState(/* do not initialize */);

  const addToLookup = (project) => {
    const newLookupItem = createLookupItem(project);
    setProjectLookup(prev => insertLookupItem(prev || [], newLookupItem));
  };

  const removeFromLookup = (projectId) => {
    setProjectLookup(prev =>
      (prev || []).filter(p => p.projectId !== projectId)
    );
  };

  const updateLookupItem = (project) => {
    setProjectLookup(prev => {
      const updatedItem = createLookupItem(project);
      const updatedItems = (prev || []).filter(item => item.projectId !== updatedItem.projectId);
      return insertLookupItem(updatedItems, updatedItem);
    });
  };

  const onProjectCreated = (project) => {
    addToLookup(project);
    selectActiveProject(project.projectId);
  };

  const onProjectDeleted = (projectId) => {
    removeFromLookup(projectId);
    if (activeProjectId === projectId) {
      setActiveProjectId(null);
    }
  };

  const onProjectUpdated = (project) => {
    updateLookupItem(project);
  };

  const selectActiveProject = (projectId) => {
    if (projectId !== activeProjectId) {
      setActiveProjectId(projectId);
    }
  };

  useEffect(() => {
    if (activeSiteId && activeSiteId > 0) {
      getProjects({ variables: { siteId: activeSiteId } });
    }
  }, [getProjects, activeSiteId]);

  useEffect(() => {
    if (projectsLoading) return;

    if (projectsError) {
      console.warn("** Failed to retrieve the project list");
      setActiveProjectId(null);
      setProjectLookup([]);
      return;
    }

    const projects = projectsData?.projects?.getProjects;
    if (projects) {
      const lookup = projects.map(createLookupItem);
      const firstProjectId = lookup[0]?.projectId;

      setActiveProjectId(firstProjectId || null);
      setProjectLookup(lookup);
    }
  }, [projectsData, projectsError, projectsLoading]);

  return {
    activeProjectId,
    onProjectCreated,
    onProjectDeleted,
    onProjectUpdated,
    projectLookup,
    projectsLoading,
    projectsError,
    selectActiveProject,
  };
};

/**
 * Helper function to create a new lookup item containing projectId and name from a project.
 *
 * @param {Object} project - The project object.
 * @param {string} project.projectId - The ID of the project.
 * @param {string} project.name - The name of the project.
 * @returns {Object} A new object with { projectId, name }.
 */
const createLookupItem = ({ projectId, name }) => ({ projectId, name });

/**
 * Helper function to insert a new project into the lookup list at the correct position,
 * maintaining alphabetical order by project name. If projects have the same name,
 * it inserts the item after the last occurrence of the same name, ordered by projectId.
 *
 * @param {Array} prevLookup - The current list of projects in the lookup.
 * @param {Object} item - The item to be inserted.
 * @returns {Array} The updated list with the new item inserted at the correct position.
 */
const insertLookupItem = (prevLookup, item) => {
  const updated = [...prevLookup];
  const insertIndex = prevLookup.findIndex(p => p.name.localeCompare(item.name) > 0);
  const adjustedInsertIndex = (insertIndex === -1) ? prevLookup.length : insertIndex;
  const lastSameNameIndex = prevLookup
    .slice(0, adjustedInsertIndex)
    .reduceRight((index, p, idx) => (p.name === item.name ? idx : index), -1);

  const finalIndex = (lastSameNameIndex === -1) ? adjustedInsertIndex : (lastSameNameIndex + 1);
  updated.splice(finalIndex, 0, item);
  return updated;
};
